import { FuturesMarginType } from '@kwenta/sdk/types'
import { DialogOverlay } from '@reach/dialog'
import { ChangeEvent, FC, memo, ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import MoonIcon from 'assets/svg/app/moon.svg'
import ResetIcon from 'assets/svg/app/reset.svg'
import SettingsIcon from 'assets/svg/app/settings.svg'
import SunIcon from 'assets/svg/app/sun.svg'
import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import SegmentedControl from 'components/SegmentedControl'
import Switch from 'components/Switch'
import { Body } from 'components/Text'
import useClickOutside from 'hooks/useClickOutside'
import { selectFuturesType } from 'state/futures/common/selectors'
import { selectCondOrdersEnabled } from 'state/futures/crossMargin/selectors'
import { setPreferences } from 'state/futures/reducer'
import {
	selectShowHistory,
	selectShowOrderLines,
	selectShowPositionLines,
	selectShowRealizedPnL,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { setTheme } from 'state/preferences/reducer'
import { selectCurrentTheme } from 'state/preferences/selectors'
import media from 'styles/media'

type ThemeType = 'dark' | 'light'

const THEME_TYPES: ThemeType[] = ['light', 'dark']

const IconByThemeType: Record<ThemeType, ReactNode> = {
	light: <SunIcon width={17} height={17} />,
	dark: <MoonIcon width={11} height={12} />,
}

const PreferencesPanel: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const futuresType = useAppSelector(selectFuturesType)
	const currentTheme = useAppSelector(selectCurrentTheme)
	const showHistory = useAppSelector(selectShowHistory)
	const showPositionLines = useAppSelector(selectShowPositionLines)
	const showOrderLines = useAppSelector(selectShowOrderLines)
	const showRealizedPnL = useAppSelector(selectShowRealizedPnL)
	const condOrdersEnabled = useAppSelector(selectCondOrdersEnabled)

	const [open, setOpen] = useState(false)

	const { ref } = useClickOutside(() => setOpen(false))
	const handleReset = useCallback(
		() =>
			dispatch(
				setPreferences({
					showRealizedPnL: true,
					showHistory: true,
					showOrderLines: true,
					showPositionLines: true,
				})
			),
		[dispatch]
	)

	const toggleTheme = useCallback(
		() => dispatch(setTheme(currentTheme === 'light' ? 'dark' : 'light')),
		[currentTheme, dispatch]
	)

	const toggleHistory = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showHistory: e.target.checked })),
		[dispatch]
	)

	const togglePositionLines = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showPositionLines: e.target.checked })),
		[dispatch]
	)

	const toggleOrderLines = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showOrderLines: e.target.checked })),
		[dispatch]
	)

	const toggleRealizedPnL = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showRealizedPnL: e.target.checked })),
		[dispatch]
	)

	const PREFERENCES = [
		{
			title: t('common.preferences.change-theme'),
			option: (
				<ThemeControl
					values={THEME_TYPES.map((o) => IconByThemeType[o])}
					selectedIndex={THEME_TYPES.indexOf(currentTheme)}
					onChange={toggleTheme}
					icon={true}
				/>
			),
		},
		{
			title: t('common.preferences.trade-history'),
			option: <Switch defaultChecked checked={showHistory} onChange={toggleHistory} />,
		},
		{
			title: t('common.preferences.chart.title'),
			subOptions: [
				{
					title: t('common.preferences.chart.positions'),
					option: <Switch checked={showPositionLines} onChange={togglePositionLines} />,
				},
				{
					title: t('common.preferences.chart.active-orders'),
					option: <Switch checked={showOrderLines} onChange={toggleOrderLines} />,
					hide: futuresType === FuturesMarginType.CROSS_MARGIN && !condOrdersEnabled,
				},
			],
		},
		{
			title: t('common.preferences.position-table.title'),
			subOptions: [
				{
					title: t('common.preferences.position-table.rpnl'),
					option: <Switch checked={showRealizedPnL} onChange={toggleRealizedPnL} />,
				},
			],
		},
	]

	return (
		<>
			<MenuButton onClick={() => setOpen((x) => !x)} noOutline>
				<SettingsIcon width={17} height={18} />
			</MenuButton>
			{open && (
				<StyledDialogOverlay>
					<PanelContainer ref={ref}>
						<CardsContainer>
							{PREFERENCES.map(({ title, option, subOptions }) => (
								<>
									{subOptions ? (
										<FlexDivCol rowGap="10px">
											<Body color="primary" weight="bold">
												{title}
											</Body>
											<FlexDivCol rowGap="10px">
												{subOptions
													.filter(({ hide }) => !hide)
													.map(({ title, option }) => (
														<FlexDivRowCentered>
															<Body color="secondary">{title}</Body>
															{option}
														</FlexDivRowCentered>
													))}
											</FlexDivCol>
										</FlexDivCol>
									) : (
										<FlexDivRowCentered>
											<Body color="primary" weight="bold">
												{title}
											</Body>
											{option}
										</FlexDivRowCentered>
									)}
									<SectionSeparator />
								</>
							))}
							<Body size="small" color="preview" weight="semiBold">
								<ResetContainer justifyContent="center" columnGap="8px" onClick={handleReset}>
									<ResetIcon width={12} height={12} />
									{t('common.preferences.reset')}
								</ResetContainer>
							</Body>
						</CardsContainer>
					</PanelContainer>
				</StyledDialogOverlay>
			)}
		</>
	)
})

const ThemeControl = styled(SegmentedControl)`
	height: 35px;
	width: 70px;
	gap: 0px;
	svg {
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.background};
	}
`

const ResetContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
`

const StyledDialogOverlay = styled(DialogOverlay)`
	z-index: 10;
	background: rgba(0, 0, 0, 0.5);
`

const SectionSeparator = styled.div`
	height: 1px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const MenuButton = styled(Button)`
	display: grid;
	place-items: center;
	height: 41px;
	width: 41px;
	padding: 0px;

	svg {
		path {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.inactive};
		}
	}
`

const PanelContainer = styled.div`
	z-index: 100;
	position: absolute;
	top: 15px;
	right: 15px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

const CardsContainer = styled(BaseCard)`
	display: flex;
	flex-direction: column;
	width: 330px;
	row-gap: 15px;
	border-radius: 8px;
	padding: 15px;
`

export default PreferencesPanel
